import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { DataStorage } from './data/data-storage';

/**
 * True when the admin is running embedded inside the Shopify Admin iframe.
 * rosie-shopify hands the iframe off to the admin with `?platform=shopify`
 * on every entry (including reloads), so this is a reliable embedded signal.
 */
export function isShopifyEmbedded(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('platform') === 'shopify';
}

@Injectable({
  providedIn: 'root'
})
export class ShopifyAppBridgeService {
  private static readonly RESUME_KEY = 'shopify-resume-path';
  private resumeHandled = false;

  // Routes that should never be treated as a "resume target". A reload always
  // re-enters at /setup, and we never want to bounce a merchant back to an auth
  // or onboarding screen — only to a real destination they had reached.
  private static readonly NON_RESUMABLE = [
    '/setup', '/login', '/register', '/forgot-password',
    '/password-reset', '/oops', '/404', '/download'
  ];

  get isShopifyEmbedded(): boolean {
    return isShopifyEmbedded();
  }

  /**
   * Records the last meaningful destination so a reload can resume there.
   * Shopify reloads the app entry (rosie-shopify), which always forwards to
   * /admin/setup, so the admin has to remember its own path.
   */
  trackNavigation(router: Router): void {
    if (!this.isShopifyEmbedded) {
      return;
    }
    router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        if (this.isResumablePath(url)) {
          DataStorage.persistent().set(ShopifyAppBridgeService.RESUME_KEY, url);
        }
      });
  }

  /**
   * On a fresh embedded entry (always /setup), if the merchant had previously
   * reached a real page, send them back to it. Mid-onboarding merchants never
   * have a stored path, so they stay on /setup as before.
   */
  maybeResume(router: Router): void {
    if (this.resumeHandled || !this.isShopifyEmbedded) {
      return;
    }
    this.resumeHandled = true;

    const resumePath = DataStorage.persistent().get(ShopifyAppBridgeService.RESUME_KEY);
    if (!resumePath || !this.isResumablePath(resumePath)) {
      return;
    }

    // Resume only if we landed on the entry route (so we don't override an
    // intentional deep link). The initial navigation may already be complete by
    // the time this runs, so handle both the settled and pending cases.
    const tryResume = () => {
      if (router.url.startsWith('/setup')) {
        router.navigateByUrl(resumePath);
      }
    };

    if (router.navigated) {
      tryResume();
    } else {
      router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), take(1))
        .subscribe(tryResume);
    }
  }

  private isResumablePath(url: string): boolean {
    return !!url && url !== '/' &&
      !ShopifyAppBridgeService.NON_RESUMABLE.some(path => url.startsWith(path));
  }
}
